// src/pages/earningsImport/RunningExpensesUpload.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { parseSalikTripsFile, parseStandardRunningExpensesFile, normalizePlateDigits } from "./runningExpensesParser";

function monthOf(dateStr) {
  return String(dateStr || "").slice(0, 7);
}

function numberFmt(value) {
  return (Number(value || 0)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function safeId(value) {
  return String(value || "")
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 140);
}

function dateMs(value, end = false) {
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (!d || Number.isNaN(d.getTime())) return 0;
  d.setHours(end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, end ? 999 : 0);
  return d.getTime();
}

function sheetMatchesRow(sheet, row) {
  const sheetDigits = normalizePlateDigits(sheet?.carNumber || sheet?.vehicleNumber);
  if (!sheetDigits || sheetDigits !== row.plateDigits) return false;

  if (sheet?.isCarryForwardOnly || sheet?.isManualDriverSheet) return false;

  const t = dateMs(row.tripDate);
  return t >= dateMs(sheet?.periodStart) && t <= dateMs(sheet?.periodEnd, true);
}

function findMatchingSheet(sheets, row) {
  const matches = (sheets || []).filter((sheet) => sheetMatchesRow(sheet, row));
  if (!matches.length) return null;

  return matches.sort((a, b) => {
    const lenA = dateMs(a.periodEnd, true) - dateMs(a.periodStart);
    const lenB = dateMs(b.periodEnd, true) - dateMs(b.periodStart);
    return lenA - lenB;
  })[0];
}

async function loadSheetsForMonth(month) {
  if (!month) return [];
  const snap = await getDocs(collection(db, "carSheets", month, "sheets"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function loadHistory(month) {
  if (!month) return [];
  const snap = await getDocs(collection(db, "runningExpenseUploads", month, "batches"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

export default function RunningExpensesUpload() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [category, setCategory] = useState("SALIK");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);

  const totalsByPlate = useMemo(() => {
    if (!preview?.rows?.length) return [];
    const map = new Map();
    preview.rows.forEach((row) => {
      const key = row.plateDigits;
      if (!map.has(key)) {
        map.set(key, { plateDigits: key, count: 0, total: 0, matched: 0 });
      }
      const item = map.get(key);
      item.count += 1;
      item.total += Number(row.amount || 0);
      if (row.matchedSheetId) item.matched += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [preview]);

  async function refreshHistory() {
    try {
      setHistory(await loadHistory(month));
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    refreshHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  async function handlePreview() {
    if (!month) return alert("Select month first.");
    if (!category) return alert("Select expense type first.");
    if (!file) return alert("Select file first.");
    setLoading(true);
    setPreview(null);

    try {
      const parsed = category === "SALIK"
        ? await parseSalikTripsFile(file)
        : await parseStandardRunningExpensesFile(file, category);
      const sheets = await loadSheetsForMonth(month);

      const rows = parsed.rows.map((row) => {
        const matchedSheet = findMatchingSheet(sheets, row);
        return {
          ...row,
          category,
          month,
          matchedSheetId: matchedSheet?.id || "",
          matchedCarNumber: matchedSheet?.carNumber || "",
        };
      });

      const outsideMonth = rows.filter((row) => monthOf(row.tripDate || row.date) !== month);
      const matchedRows = rows.filter((row) => row.matchedSheetId);
      const unmatchedRows = rows.filter((row) => !row.matchedSheetId);
      const totalAmount = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

      setPreview({
        category,
        month,
        fileName: file.name,
        headerRowNumber: parsed.headerRowNumber,
        totalRowsInSheet: parsed.totalRowsInSheet,
        rows,
        matchedRows,
        unmatchedRows,
        outsideMonth,
        totalAmount,
      });
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to parse running expenses file.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!preview?.rows?.length) return alert("Preview first.");

    if (preview.outsideMonth.length > 0) {
      const ok = window.confirm(
        `${preview.outsideMonth.length} row(s) have Date outside ${month}. Continue saving?`
      );
      if (!ok) return;
    }

    const ok = window.confirm(
      `Save ${preview.rows.length} ${category} rows?\nMatched rows: ${preview.matchedRows.length}\nUnmatched rows: ${preview.unmatchedRows.length}\nTotal: AED ${numberFmt(preview.totalAmount)}`
    );
    if (!ok) return;

    setLoading(true);

    try {
      const uploadBatchId = String(Date.now());
      let batch = writeBatch(db);
      let batchCount = 0;
      let saved = 0;

      const commitIfNeeded = async (force = false) => {
        if (batchCount >= 450 || (force && batchCount > 0)) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      };

      for (const row of preview.rows) {
        const naturalId =
          row.transactionId ||
          row.reference ||
          `${row.tripDate || row.date}_${row.tripTime || ""}_${row.plateDigits}_${row.amount}_${row.sourceRowNumber}`;
        const rowId = safeId(`${category}_${naturalId}`);
        const ref = doc(db, "carRunningExpenses", month, "rows", rowId);

        batch.set(
          ref,
          {
            ...row,
            uploadBatchId,
            sourceFileName: preview.fileName,
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
        batchCount += 1;
        saved += 1;
        await commitIfNeeded(false);
      }

      const batchRef = doc(db, "runningExpenseUploads", month, "batches", uploadBatchId);
      batch.set(
        batchRef,
        {
          uploadBatchId,
          month,
          category,
          sourceFileName: preview.fileName,
          totalRows: preview.rows.length,
          matchedRows: preview.matchedRows.length,
          unmatchedRows: preview.unmatchedRows.length,
          outsideMonthRows: preview.outsideMonth.length,
          totalAmount: Number(preview.totalAmount.toFixed(2)),
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
      batchCount += 1;
      await commitIfNeeded(true);

      alert(
        category === "TRAFFIC FINE"
          ? `Saved ${saved} Traffic Fine row(s). They are available in the Daily Income Report and do not change Car Sheets.`
          : `Saved ${saved} rows. Now refresh/rebuild car sheets for ${month} to apply ${category}.`
      );
      setPreview(null);
      setFile(null);
      await refreshHistory();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save running expenses.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteBatch(item) {
    const ok = window.confirm(
      `Delete ${item.category} batch ${item.uploadBatchId}?\nThis deletes the uploaded expense rows only.${item.category === "TRAFFIC FINE" ? " Traffic Fine rows affect the Daily Income Report only." : " Rebuild car sheets after delete to remove the amount from sheets."}`
    );
    if (!ok) return;

    setLoading(true);
    try {
      const rowsQ = query(
        collection(db, "carRunningExpenses", month, "rows"),
        where("uploadBatchId", "==", item.uploadBatchId)
      );
      const rowsSnap = await getDocs(rowsQ);

      let batch = writeBatch(db);
      let batchCount = 0;
      for (const rowDoc of rowsSnap.docs) {
        batch.delete(rowDoc.ref);
        batchCount += 1;
        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      if (batchCount > 0) await batch.commit();

      await deleteDoc(doc(db, "runningExpenseUploads", month, "batches", item.uploadBatchId));
      alert(`Deleted ${rowsSnap.size} running expense row(s). Rebuild car sheets to refresh totals.`);
      await refreshHistory();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to delete batch.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ marginTop: 0 }}>Running Expenses Upload</h2>
      <p style={{ color: "#555", maxWidth: 920 }}>
        For Salik, upload the downloaded Salik file. It uses <b>Trip Date</b>, matches car by plate digits only,
        and takes amount from <b>Total Amount incl. VAT</b>. For Darb, ENOC/Petrol, Parking, and Traffic Fines, use the standard format:
        <b>Date, Car Number, Amount</b> (Traffic Fines also accepts <b>Fine Amount</b>). Traffic Fine uploads are used by the Daily Income Report only and do not change Car Sheets.
      </p>

      <div style={cardStyle}>
        <div style={rowStyle}>
          <label style={labelStyle}>Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={inputStyle} />

          <label style={labelStyle}>Expense type</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
            <option value="SALIK">SALIK</option>
            <option value="DARB">DARB</option>
            <option value="PETROL">PETROL / ENOC</option>
            <option value="PARKING">PARKING</option>
            <option value="TRAFFIC FINE">TRAFFIC FINE (Daily Income Report only)</option>
          </select>

          <input
            type="file"
            accept=".xls,.xlsx,.csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ ...inputStyle, minWidth: 280 }}
          />

          <button disabled={loading} onClick={handlePreview} style={buttonStyle}>
            {loading ? "Please wait..." : "Preview Upload"}
          </button>
        </div>
      </div>

      {preview && (
        <div style={cardStyle}>
          <h3>Preview</h3>
          <div style={summaryGridStyle}>
            <div><b>File</b><br />{preview.fileName}</div>
            <div><b>Total rows</b><br />{preview.rows.length}</div>
            <div><b>{category === "TRAFFIC FINE" ? "Car Sheet matched (reference)" : "Matched rows"}</b><br />{preview.matchedRows.length}</div>
            <div><b>{category === "TRAFFIC FINE" ? "No Car Sheet match" : "Unmatched rows"}</b><br />{preview.unmatchedRows.length}</div>
            <div><b>Outside selected month</b><br />{preview.outsideMonth.length}</div>
            <div><b>Total amount</b><br />AED {numberFmt(preview.totalAmount)}</div>
          </div>

          {preview.outsideMonth.length > 0 && (
            <div style={warningStyle}>
              Warning: {preview.outsideMonth.length} row(s) have Date outside {month}. For Salik, the
              parser uses Trip Date, not Transaction Post Date.
            </div>
          )}

          {preview.unmatchedRows.length > 0 && (
            <div style={warningStyle}>
              {category === "TRAFFIC FINE"
                ? `${preview.unmatchedRows.length} fine row(s) do not match an existing Car Sheet. You can still save them: the Daily Income Report will match the car number and fine date against driver assignments.`
                : `${preview.unmatchedRows.length} row(s) could not be matched to a car sheet. Usually this means the car sheet for that plate/date period does not exist yet.`}
            </div>
          )}

          <h4>Totals by plate</h4>
          <div style={{ maxHeight: 260, overflow: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Plate digits</th>
                  <th style={thStyle}>Rows</th>
                  <th style={thStyle}>Matched</th>
                  <th style={thStyle}>Total AED</th>
                </tr>
              </thead>
              <tbody>
                {totalsByPlate.map((r) => (
                  <tr key={r.plateDigits}>
                    <td style={tdStyle}>{r.plateDigits}</td>
                    <td style={tdStyle}>{r.count}</td>
                    <td style={tdStyle}>{r.matched}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{numberFmt(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.unmatchedRows.length > 0 && (
            <>
              <h4>First unmatched rows</h4>
              <div style={{ maxHeight: 220, overflow: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Plate</th>
                      <th style={thStyle}>Amount</th>
                      <th style={thStyle}>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.unmatchedRows.slice(0, 20).map((r, idx) => (
                      <tr key={`${r.transactionId}_${idx}`}>
                        <td style={tdStyle}>{r.tripDate || r.date}</td>
                        <td style={tdStyle}>{r.plate}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{numberFmt(r.amount)}</td>
                        <td style={tdStyle}>{r.transactionId || r.reference || r.remarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <button disabled={loading} onClick={handleSave} style={{ ...buttonStyle, marginTop: 16 }}>
            {category === "TRAFFIC FINE" ? "Save Traffic Fines" : "Save Running Expenses"}
          </button>
        </div>
      )}

      <div style={cardStyle}>
        <h3>Upload History</h3>
        {history.length === 0 ? (
          <p style={{ color: "#777" }}>No running expense batches found for {month}.</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Batch ID</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Rows</th>
                <th style={thStyle}>Matched</th>
                <th style={thStyle}>Unmatched</th>
                <th style={thStyle}>Total AED</th>
                <th style={thStyle}>File</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.uploadBatchId}>
                  <td style={tdStyle}>{h.uploadBatchId}</td>
                  <td style={tdStyle}>{h.category}</td>
                  <td style={tdStyle}>{h.totalRows}</td>
                  <td style={tdStyle}>{h.matchedRows}</td>
                  <td style={tdStyle}>{h.unmatchedRows}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{numberFmt(h.totalAmount)}</td>
                  <td style={tdStyle}>{h.sourceFileName}</td>
                  <td style={tdStyle}>
                    <button disabled={loading} onClick={() => handleDeleteBatch(h)} style={dangerButtonStyle}>
                      Delete Batch
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const rowStyle = {
  display: "flex",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const labelStyle = { fontWeight: 600, color: "#333" };
const inputStyle = { padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6 };
const buttonStyle = {
  padding: "9px 14px",
  border: "none",
  borderRadius: 6,
  background: "#2563eb",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};
const dangerButtonStyle = { ...buttonStyle, background: "#dc2626", padding: "6px 10px" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { background: "#f3f4f6", border: "1px solid #ddd", padding: 8, textAlign: "left" };
const tdStyle = { border: "1px solid #e5e7eb", padding: 7 };
const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 12,
  marginBottom: 12,
};
const warningStyle = {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  padding: 10,
  borderRadius: 6,
  margin: "10px 0",
};
